import React, { useMemo, useEffect, useState } from 'react';
import { AgendaItem } from '@/types';
import { sameDay } from '@/lib/dateUtils';
import { TONE_DOT } from './EventPill';

interface Props {
  cursor: Date;
  byDay: Record<string, AgendaItem[]>;
  onDayClick: (d: Date) => void;
}

const DOW_LABELS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const HOUR_START = 7;
const HOUR_END = 19;
const HOUR_HEIGHT = 56;

function startOfWeek(d: Date): Date {
  const w = new Date(d);
  w.setDate(w.getDate() - w.getDay());
  w.setHours(0, 0, 0, 0);
  return w;
}

export function WeekView({ cursor, byDay, onDayClick }: Props) {
  const [nowOffset, setNowOffset] = useState<number | null>(null);

  const weekDays = useMemo(() => {
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      return d;
    });
  }, [cursor]);

  const today = new Date();
  const todayIndex = weekDays.findIndex(d => sameDay(d, today));

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const h = now.getHours() + now.getMinutes() / 60;
      if (h < HOUR_START || h > HOUR_END) { setNowOffset(null); return; }
      setNowOffset((h - HOUR_START) * HOUR_HEIGHT);
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, []);

  const hours = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);

  return (
    <div className="relative">
      {/* Cabeçalho */}
      <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-line dark:border-line-dark">
        <div />
        {weekDays.map((d, i) => {
          const isWkd = i === 0 || i === 6;
          const isToday = sameDay(d, today);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(d)}
              className={[
                'px-2 py-2 text-left transition-colors',
                isToday ? 'bg-brand-50 dark:bg-brand-500/10' : 'hover:bg-surface-alt dark:hover:bg-surface-dark-alt',
              ].join(' ')}
            >
              <div className={[
                'text-[10.5px] font-semibold uppercase tracking-[0.08em]',
                isWkd ? 'text-ink-placeholder dark:text-ink-placeholder-dark' : 'text-ink-subtitle dark:text-ink-subtitle-dark',
              ].join(' ')}>
                {DOW_LABELS[i]}
              </div>
              <div className={[
                'text-[18px] font-extralight tabular-nums',
                isToday ? 'text-brand-700 dark:text-brand-300 font-semibold' : 'text-ink-title dark:text-ink-title-dark',
              ].join(' ')}>
                {d.getDate()}
              </div>
            </button>
          );
        })}
      </div>

      {/* Grid de horas */}
      <div className="relative grid grid-cols-[56px_repeat(7,1fr)]">
        {/* Coluna de horas */}
        <div className="flex flex-col">
          {hours.map(h => (
            <div
              key={h}
              className="text-[10px] text-ink-placeholder dark:text-ink-placeholder-dark tabular-nums pt-1 pr-1.5 text-right border-t border-line dark:border-line-dark"
              style={{ height: HOUR_HEIGHT }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* Colunas dos dias */}
        {weekDays.map((d, i) => {
          const isToday = sameDay(d, today);
          const dayEvents = byDay[d.toDateString()] ?? [];
          return (
            <div
              key={i}
              className={[
                'relative border-l border-line dark:border-line-dark',
                isToday ? 'bg-brand-50/30 dark:bg-brand-500/5' : '',
              ].join(' ')}
            >
              {hours.map(h => (
                <div key={h} className="border-t border-line dark:border-line-dark" style={{ height: HOUR_HEIGHT }} />
              ))}
              {dayEvents.filter(e => (e.hour ?? 9) >= HOUR_START && (e.hour ?? 9) <= HOUR_END).map(e => {
                const top = ((e.hour ?? 9) - HOUR_START) * HOUR_HEIGHT + 2;
                return (
                  <div
                    key={e.id}
                    className="absolute left-1 right-1 p-1.5 rounded-md bg-surface dark:bg-surface-dark border border-line dark:border-line-dark text-[11px] shadow-sm-warm dark:shadow-sm-dark overflow-hidden animate-fade-in"
                    style={{ top }}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TONE_DOT[e.type]}`} />
                      <span className="text-ink-title dark:text-ink-title-dark font-medium truncate">
                        {e.title.split('—')[0].trim()}
                      </span>
                    </div>
                    {e.subtitle && (
                      <div className="text-[10px] text-ink-subtitle dark:text-ink-subtitle-dark truncate mt-0.5">
                        {e.subtitle}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Linha do "agora" */}
        {nowOffset !== null && todayIndex >= 0 && (
          <div
            className="absolute left-[56px] right-0 h-px bg-brand-500 z-10 pointer-events-none"
            style={{ top: nowOffset }}
          >
            <div
              className="absolute w-2 h-2 rounded-full bg-brand-500 -translate-y-[3px]"
              style={{ left: `calc(${(todayIndex / 7) * 100}% - 3px)` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
